import { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, X, PackagePlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { fmtDate } from '@/lib/utils';
import SupplierMappingDialog from '@/components/SupplierMappingDialog';

const MODES = {
  sales: {
    endpoint: '/upload',
    dropLabel: 'sales file',
    fields: ['date', 'product_name', 'quantity_sold', 'supplier', 'barcode', 'mrp'],
    labels: { date: 'date', product_name: 'product', quantity_sold: 'qty sold', supplier: 'supplier', barcode: 'barcode', mrp: 'mrp' }
  },
  purchase: {
    endpoint: '/purchase-upload',
    dropLabel: 'purchase report',
    fields: ['date', 'product_name', 'quantity_purchased', 'supplier', 'barcode', 'mrp'],
    labels: { date: 'date', product_name: 'product', quantity_purchased: 'qty purchased', supplier: 'supplier', barcode: 'barcode', mrp: 'mrp' }
  }
};

export default function UploadZone({ mode = 'sales', onUploadSuccess }) {
  const cfg = MODES[mode];
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [mappingData, setMappingData] = useState(null);
  const [dupWarning, setDupWarning] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (file, force = false) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Only .csv and .xlsx files are supported');
      return;
    }

    setUploading(true);
    setResult(null);
    setDupWarning(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await api.post(`${cfg.endpoint}${force ? '?force=true' : ''}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (data.status === 'duplicate_warning') {
        setDupWarning({ file, info: data.duplicate_of });
        return;
      }
      if (mode === 'purchase' && data.status === 'needs_supplier_mapping') {
        if (data.barcode_warning) toast.warning(`Barcode health check: ${data.barcode_warning}`, { duration: 12000 });
        setMappingData(data);
        return;
      }
      setResult(data);
      toast.success(`Uploaded ${data.cleaned_rows} records from ${file.name}`);
      if (data.barcode_warning) toast.warning('Barcode health check flagged this file — see details below', { duration: 8000 });
      onUploadSuccess?.();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleMappingConfirmed = (data) => {
    setMappingData(null);
    setResult(data);
    toast.success(`Imported ${data.cleaned_rows} purchase records`);
    if (data.barcode_warning) toast.warning('Barcode health check flagged this file — see details below', { duration: 8000 });
    onUploadSuccess?.();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  return (
    <div className="space-y-4">
      <div
        data-testid={`upload-dropzone-${mode}`}
        className={`upload-dropzone border-2 border-dashed bg-zinc-50/50 p-8 sm:p-12 flex flex-col items-center justify-center cursor-pointer min-h-[160px] transition-all ${
          dragging ? 'border-emerald-400 bg-emerald-50/50' : 'border-zinc-300 hover:border-emerald-400 hover:bg-emerald-50/30'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          data-testid={`upload-file-input-${mode}`}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-zinc-600 font-medium">Processing file...</span>
          </div>
        ) : (
          <>
            <Upload className="h-8 w-8 text-zinc-400 mb-3" />
            <p className="text-sm text-zinc-600 font-medium">
              Drop your {cfg.dropLabel} here or <span className="text-emerald-600 underline">browse</span>
            </p>
            <p className="text-xs text-zinc-400 mt-1">Supports .csv and .xlsx files</p>
          </>
        )}
      </div>

      {dupWarning && (
        <div data-testid="duplicate-warning" className="border border-amber-300 bg-amber-50/70 p-4 animate-fade-in">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-zinc-900">This file was already uploaded</p>
              <p className="text-xs text-zinc-600 mt-0.5">
                Its content is identical to <span className="font-medium">{dupWarning.info.filename}</span> uploaded on{' '}
                {fmtDate(dupWarning.info.uploaded_at, true)}. Uploading again will count these {mode === 'purchase' ? 'purchases' : 'sales'} twice.
              </p>
              <div className="flex items-center gap-2 mt-2.5">
                <button
                  data-testid="duplicate-upload-anyway"
                  onClick={() => handleFile(dupWarning.file, true)}
                  className="text-xs font-semibold px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                >
                  Upload Anyway
                </button>
                <button
                  data-testid="duplicate-cancel"
                  onClick={() => setDupWarning(null)}
                  className="text-xs font-semibold px-3 py-1.5 border border-zinc-300 text-zinc-600 hover:bg-zinc-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div
          data-testid="upload-result"
          className={`border p-4 animate-fade-in ${
            result.status === 'success' ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {result.status === 'success' ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              )}
              <div>
                <p className="text-sm font-medium text-zinc-900">
                  <FileSpreadsheet className="h-4 w-4 inline mr-1.5 text-zinc-400" />
                  {result.filename}
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-zinc-500">
                  <span>Total rows: <span className="font-mono font-semibold text-zinc-700">{result.total_rows}</span></span>
                  <span>Cleaned: <span className="font-mono font-semibold text-emerald-700">{result.cleaned_rows}</span></span>
                  {result.flagged_rows > 0 && (
                    <span>Flagged: <span className="font-mono font-semibold text-amber-700">{result.flagged_rows}</span></span>
                  )}
                  {result.new_products > 0 && (
                    <span data-testid="new-products-count" className="flex items-center gap-1 text-sky-700">
                      <PackagePlus className="h-3 w-3" />
                      New products added: <span className="font-mono font-semibold">{result.new_products}</span>
                    </span>
                  )}
                </div>
                {/* Column mapping display */}
                {result.column_mapping && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {cfg.fields.map(field => {
                      const mapped = result.column_mapping[field];
                      const label = cfg.labels[field] || field;
                      return (
                        <span key={field} className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 border font-medium ${
                          mapped ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'
                        }`}>
                          {label}: {mapped ? <span className="font-semibold">{mapped}</span> : 'not found'}
                        </span>
                      );
                    })}
                  </div>
                )}
                {result.barcode_warning && (
                  <div data-testid="barcode-warning" className="mt-2 border border-red-200 bg-red-50/70 px-2.5 py-1.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-red-700 mb-0.5 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Barcode Health Check
                    </p>
                    <p className="text-[11px] text-red-700">{result.barcode_warning}</p>
                  </div>
                )}
                {result.pacer_report?.length > 0 && (
                  <div data-testid="upload-pacer-report" className="mt-2 border border-amber-200 bg-amber-50/60 px-2.5 py-1.5">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-0.5">Auto-cleanup applied (Pacer)</p>
                    {result.pacer_report.map((line, i) => (
                      <p key={`${i}-${line}`} className="text-[11px] text-amber-800">• {line}</p>
                    ))}
                  </div>
                )}
                {result.unmapped_columns?.length > 0 && (
                  <p className="text-xs text-zinc-400 mt-1.5">
                    Cleared extra columns: <span className="italic">{result.unmapped_columns.join(', ')}</span>
                  </p>
                )}
                {result.missing_fields?.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Missing fields (defaults used): {result.missing_fields.join(', ')}
                  </p>
                )}
              </div>
            </div>
            <button onClick={() => setResult(null)} className="text-zinc-400 hover:text-zinc-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {mappingData && (
        <SupplierMappingDialog
          data={mappingData}
          onConfirmed={handleMappingConfirmed}
          onCancelled={() => { setMappingData(null); toast.info('Upload cancelled — no data was imported'); }}
        />
      )}
    </div>
  );
}
